'use client'

import useEmblaCarousel from 'embla-carousel-react'
import { Children, useContext, useMemo } from 'react'

import {
  ImageSliderDotButton,
  useDotButton,
} from './components/ImageSliderDotButton'
import { ImageSliderContext } from './context/ImageSliderContext'
import { cn, warnIfEmptyLabel } from '@/utils'

export type ImageSliderProps = Omit<
  React.ComponentProps<'div'>,
  'aria-label'
> & {
  /**
   * Accessible name for the image gallery region. Required — provide a
   * localized string from the consuming app.
   */
  ariaLabel: string
}

/**
 * ImageSlider root component.
 * It wraps the ImageSlides and ImageSliderScrollBar components.
 */
const ImageSlider = ({ className, ariaLabel, ...props }: ImageSliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel()

  warnIfEmptyLabel(ariaLabel, 'ImageSlider: ariaLabel')

  return (
    <ImageSliderContext.Provider value={{ emblaApi }}>
      <section
        className={cn(
          'ImageSlider__root',
          'embla relative w-full overflow-hidden',
          className,
        )}
        aria-label={ariaLabel}
        {...props}
        ref={emblaRef}
      />
    </ImageSliderContext.Provider>
  )
}

/**
 * ImageSlides component.
 * It wraps the ImageSlide components and handles the scroll event.
 */
const ImageSlides = ({
  className,
  children,
  dotsClassName,
  prerenderDots = false,
  dotsAriaLabel,
  formatDotAriaLabel,
  ...props
}: React.ComponentProps<'ul'> & {
  dotsClassName?: string
  prerenderDots?: boolean
  /**
   * Accessible name for the slide-dots navigation. Required — provide a
   * localized string from the consuming app.
   */
  dotsAriaLabel: string
  /**
   * Builds the accessible name for each dot. Receives the zero-based slide
   * index and the total slide count; the consuming app formats and localizes
   * the string. Required.
   */
  formatDotAriaLabel: (index: number, total: number) => string
}) => {
  const childrenCount = Children.count(children)

  return (
    <>
      <ul
        className={cn(
          'ImageSlider__slides embla__container flex h-[480px] list-none',
          childrenCount <= 1 && 'justify-center',
          className,
        )}
        {...props}
        style={{
          scrollbarWidth: 'none', // hiding scrollbars for firefox,
          ...props.style,
        }}
        data-testid="image-slider-slides"
      >
        {children}
      </ul>

      <div className="embla__controls">
        <ImageSliderDots
          className={dotsClassName}
          prerenderDots={prerenderDots}
          numberOfSlides={childrenCount}
          ariaLabel={dotsAriaLabel}
          formatDotAriaLabel={formatDotAriaLabel}
        />
      </div>
    </>
  )
}

/**
 * ImageSliderDots component.
 * It shows the dots navigation for the ImageSlider.
 */
const ImageSliderDots = ({
  className,
  prerenderDots,
  numberOfSlides,
  ariaLabel,
  formatDotAriaLabel,
}: {
  className?: string
  prerenderDots?: boolean
  numberOfSlides: number
  ariaLabel: string
  formatDotAriaLabel: (index: number, total: number) => string
}) => {
  const { emblaApi } = useContext(ImageSliderContext)
  const dotButton = useDotButton(emblaApi)

  warnIfEmptyLabel(ariaLabel, 'ImageSlides: dotsAriaLabel')

  const snaps = useMemo(() => {
    // enable prerender dots if enabled and there are no scroll snaps
    // to avoid layout shifts while working with server-side-rendering
    if (prerenderDots) {
      return Array.from(
        { length: dotButton.scrollSnaps.length || numberOfSlides },
        (_, index) => index,
      )
    }

    return dotButton.scrollSnaps.map((snap) => snap)
  }, [prerenderDots, numberOfSlides, dotButton.scrollSnaps])

  const selectedIndex = dotButton.selectedIndex || 0

  const totalSlides = dotButton.scrollSnaps.length || numberOfSlides

  return (
    <div
      className={cn(
        'embla__dots ImageSlider__dots flex items-center justify-center gap-1 py-2',
        className,
      )}
      role="tablist"
      aria-label={ariaLabel}
      data-testid="image-slider-dots"
    >
      {snaps.map((_, index) => (
        <ImageSliderDotButton
          key={index}
          index={index}
          totalSlides={totalSlides}
          onClick={() => dotButton.onDotButtonClick(index)}
          isActive={index === selectedIndex}
          currentSlide={selectedIndex}
          formatDotAriaLabel={formatDotAriaLabel}
        />
      ))}
    </div>
  )
}

const slideSizeClassName = 'w-[320px]'

/**
 * ImageSlide component.
 * It wraps a slide image and handles his position.
 */
const ImageSlide = ({ className, ...props }: React.ComponentProps<'li'>) => {
  return (
    <li
      className={cn(
        'ImageSlider__slide embla__slide relative',
        'not-first:ml-2 min-w-0 flex-[0_0_var(--slider-size)]',
        className,
      )}
      style={
        {
          '--slider-size': 'auto',
          ...props.style,
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

/**
 * ImageSliderOverlay component.
 * Can be used to add content on top of the ImageSlides.
 */
const ImageSliderOverlay = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    className={cn(
      slideSizeClassName,
      'ImageSlider__overlay',
      'pointer-events-none absolute left-[50%] top-0 h-[calc(100%-20px)] translate-x-[-50%]',
      className,
    )}
    {...props}
  >
    {Children.map(children, (child) => (
      <div style={{ pointerEvents: 'auto' }}>{child}</div>
    ))}
  </div>
)

export { ImageSlide, ImageSlider, ImageSliderOverlay, ImageSlides }
